import { useState } from 'react';
import {
  Stack,
  Text,
  Button,
  NumberInput,
  Code,
  Alert,
  ScrollArea,
  Group,
  CopyButton,
  ActionIcon,
  Tooltip,
  Select,
  Loader,
} from '@mantine/core';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import CompanyWrapper from 'src/company/templates/components/CompanyWrapper';
import { Company } from 'src/secret-menu/hooks/useCompanies';

interface AudioFileSummary {
  id: string;
  file_name: string;
  file_type: string | null;
  created_at: string | null;
}

interface AudioFileListResponse {
  audio_files: AudioFileSummary[];
}

interface TopicTreeResponse {
  tree: {
    root: object;
    total_segments: number;
    max_leaf_size: number;
    total_leaves: number;
    max_depth: number;
  };
}

export default function TopicTreeTab() {
  return <CompanyWrapper contentComponent={TopicTreeContent} />;
}

function TopicTreeContent({ company }: { company: Company }) {
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [maxLeafSize, setMaxLeafSize] = useState<number>(15);
  const [result, setResult] = useState<TopicTreeResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    data: audioFilesData,
    isLoading: isLoadingFiles,
    isError: isFilesError,
    error: filesError,
  } = useQuery<AudioFileListResponse>({
    queryKey: ['admin', 'companies', company.id, 'topic-tree', 'audio-files'],
  });

  const { mutateAsync, isPending: isLoading } = useMutation<
    { data: TopicTreeResponse },
    Error,
    { audioFileId: string; maxLeafSize: number }
  >({
    mutationFn: ({ audioFileId, maxLeafSize: leafSize }) =>
      api.post(
        `admin/companies/${company.id}/topic-tree/${audioFileId}`,
        {
          max_leaf_size: leafSize,
        },
        { timeoutMs: 120000 },
      ),
  });

  const fileOptions =
    audioFilesData?.audio_files.map(f => ({
      value: f.id,
      label: f.file_name,
    })) ?? [];

  const resultJson = result ? JSON.stringify(result.tree, null, 2) : '';

  const handleBuild = async () => {
    if (!selectedFileId) return;
    setErrorMessage(null);
    try {
      const response = await mutateAsync({
        audioFileId: selectedFileId,
        maxLeafSize,
      });
      setResult(response.data);
    } catch (e) {
      setResult(null);
      setErrorMessage(String(e));
    }
  };

  return (
    <Stack p="md">
      <Text size="xl" fw={700}>
        Topic Tree Builder
      </Text>
      <Text c="dimmed">
        Build a hierarchical topic tree from a meeting transcript using embedding-based clustering
      </Text>

      {isFilesError && (
        <Alert color="red" title="Failed to load audio files">
          {String(filesError)}
        </Alert>
      )}

      {isLoadingFiles ? (
        <Group>
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading audio files...
          </Text>
        </Group>
      ) : (
        <>
          <Text size="xs" c="dimmed">
            {fileOptions.length} audio file(s) found for {company.name}
          </Text>
          <Select
            label="Audio File"
            placeholder="Select an audio file"
            data={fileOptions}
            value={selectedFileId}
            onChange={setSelectedFileId}
            searchable
            clearable
            w={400}
          />
        </>
      )}

      <NumberInput
        label="Max leaf size"
        description="Maximum number of segments per leaf node"
        value={maxLeafSize}
        onChange={val => setMaxLeafSize(Number(val) || 15)}
        min={3}
        max={50}
        w={200}
      />

      <Button onClick={handleBuild} loading={isLoading} disabled={!selectedFileId} w={200}>
        Build Topic Tree
      </Button>

      {errorMessage && (
        <Alert color="red" title="Error">
          {errorMessage}
        </Alert>
      )}

      {result && (
        <Stack>
          <Group>
            <Text fw={600}>
              {result.tree.total_leaves} leaves | depth {result.tree.max_depth} |{' '}
              {result.tree.total_segments} segments
            </Text>
            <CopyButton value={resultJson}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? 'Copied' : 'Copy JSON'}>
                  <ActionIcon color={copied ? 'teal' : 'gray'} variant="subtle" onClick={copy}>
                    {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>
          <ScrollArea h={600}>
            <Code block>{resultJson}</Code>
          </ScrollArea>
        </Stack>
      )}
    </Stack>
  );
}
